import { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BadgeCheck, MapPin, Star, ShieldCheck, Package, MessageCircle, ShoppingCart, Minus, Plus, ChevronLeft, ChevronRight, Store } from "lucide-react";
import { api, mediaUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { useCurrency } from "../context/CurrencyContext";
import { useCart } from "../context/CartContext";
import { useSiteConfig } from "../context/ConfigContext";
import ProductCard from "../components/ProductCard";
import QuoteDialog from "../components/QuoteDialog";
import OrderDialog from "../components/OrderDialog";
import ChatDialog from "../components/ChatDialog";
import { useLiveStreams, sellerLiveStream } from "../lib/useLive";

export default function ProductDetail() {
  const { t } = useLang();
  const { user } = useAuth();
  const { fmt, code } = useCurrency();
  const { refreshCart } = useCart();
  const liveStreams = useLiveStreams();
  const { features } = useSiteConfig();
  const navigate = useNavigate();
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [active, setActive] = useState(0);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [orderOpen, setOrderOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [color, setColor] = useState("");
  const [size, setSize] = useState("");
  const [volume, setVolume] = useState("");
  const [qty, setQty] = useState(1);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState("");
  const touchRef = useRef(null);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [tierIdx, setTierIdx] = useState(0);

  // Price formatted with 2 decimals for USD-like currencies, as in the app
  const fmtTier = (usd) => {
    if (usd === null || usd === undefined) return "";
    const s = fmt(usd);
    return ["USD", "EUR", "GBP"].includes(code) && !s.includes(".") ? `${s}.00` : s;
  };

  // Preload the next gallery photo so swiping feels instant
  useEffect(() => {
    const gallery = product?.images || [];
    if (gallery[active + 1]) {
      const im = new Image();
      im.src = mediaUrl(gallery[active + 1].url);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, product?.id]);

  useEffect(() => {
    setProduct(null);
    setActive(0);
    setImgLoaded(false);
    setColor("");
    setSize("");
    setVolume("");
    api(`/products/${id}`)
      .then((p) => {
        setProduct(p);
        setQty(p.moq || 1);
        try {
          const key = "simashi_history";
          const prev = JSON.parse(localStorage.getItem(key) || "[]").filter((x) => x !== p.id);
          localStorage.setItem(key, JSON.stringify([p.id, ...prev].slice(0, 30)));
        } catch {}
        if (p.category) {
          api(`/products?category=${p.category}&limit=5`)
            .then((r) => setRelated(r.filter((x) => x.id !== p.id).slice(0, 4)))
            .catch(() => {});
        }
      })
      .catch((e) => setError(e.message));
  }, [id]);

  if (error)
    return (
      <div data-testid="product-error" className="max-w-7xl mx-auto px-4 py-24 text-center">
        <p className="font-heading text-xl font-semibold text-slate-800">{t("empty_t")}</p>
        <p className="mt-2 text-sm text-slate-500">{error}</p>
        <Link to="/products" className="mt-6 inline-block rounded-full bg-teal-700 px-5 py-2.5 text-sm font-semibold text-white">
          {t("nav_products")}
        </Link>
      </div>
    );

  if (!product)
    return (
      <div data-testid="product-loading" className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 gap-10 animate-pulse">
          <div className="aspect-square rounded-3xl bg-slate-100" />
          <div className="space-y-4 pt-4">
            <div className="h-8 w-3/4 rounded bg-slate-100" />
            <div className="h-6 w-1/3 rounded bg-slate-100" />
            <div className="h-24 rounded bg-slate-100" />
          </div>
        </div>
      </div>
    );

  const gallery = product.images?.length ? product.images : product.image ? [{ url: product.image }] : [];
  const liveStream = sellerLiveStream(liveStreams, { id: product.supplier_id, name: product.supplier_name });

  const priceTiers = product.price_tiers?.length
    ? product.price_tiers
    : [{ min_qty: product.moq || 1, price: product.price_min ?? product.price_max }];

  const addToCart = async () => {
    if (!user) {
      navigate(`/login?next=/product/${product.id}`);
      return;
    }
    if (product.colors?.length && !color) {
      toast.error(t("choose_color"));
      return;
    }
    if (product.sizes?.length && !size) {
      toast.error(t("choose_size"));
      return;
    }
    setAdding(true);
    try {
      let delivery = null;
      const addrs = await api("/me/addresses", { auth: true }).catch(() => []);
      const def = addrs.find((a) => a.is_default) || addrs[0];
      if (def) delivery = { country: def.country, region: def.region, city: def.city };
      if (!delivery) {
        toast.error(t("need_addr_cart"));
        navigate("/account");
        return;
      }
      await api("/cart/items", {
        method: "POST",
        auth: true,
        body: { product_id: product.id, quantity: Number(qty), color, size, volume, delivery },
      });
      toast.success(t("added_cart"));
      refreshCart();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setAdding(false);
    }
  };

  return (
    <div data-testid="product-detail-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <nav className="text-xs text-slate-500 mb-6">
        <Link to="/" className="hover:text-teal-700">MARHINE</Link> /{" "}
        <Link to="/products" className="hover:text-teal-700">{t("nav_products")}</Link> /{" "}
        <Link to={`/products?category=${product.category}`} className="hover:text-teal-700 capitalize">{product.category}</Link>
      </nav>
      <div className="grid md:grid-cols-2 gap-10">
        <div>
          <div
            data-testid="product-gallery"
            className="relative overflow-hidden rounded-3xl border border-slate-200 bg-teal-50 aspect-square select-none touch-pan-y"
            onTouchStart={(e) => { touchRef.current = e.touches[0].clientX; }}
            onTouchEnd={(e) => {
              if (touchRef.current === null) return;
              const dx = e.changedTouches[0].clientX - touchRef.current;
              touchRef.current = null;
              if (dx < -45 && active < gallery.length - 1) { setActive(active + 1); setImgLoaded(false); }
              else if (dx > 45 && active > 0) { setActive(active - 1); setImgLoaded(false); }
            }}
          >
            {gallery[active] ? (
              <img
                key={active}
                data-testid="product-main-image"
                src={mediaUrl(gallery[active].url)}
                alt={product.title}
                draggable={false}
                onLoad={() => setImgLoaded(true)}
                className={`h-full w-full object-cover transition-opacity duration-300 ${imgLoaded ? "opacity-100" : "opacity-0"}`}
              />
            ) : (
              <div className="flex h-full items-center justify-center text-teal-300">
                <Package className="h-16 w-16" />
              </div>
            )}
            {gallery.length > 1 && (
              <>
                <button
                  data-testid="gallery-prev"
                  onClick={() => { setActive((a) => Math.max(0, a - 1)); setImgLoaded(false); }}
                  disabled={active === 0}
                  aria-label="Previous photo"
                  className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-white/85 p-2 text-slate-700 shadow-md backdrop-blur transition hover:bg-white active:scale-90 disabled:opacity-0"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  data-testid="gallery-next"
                  onClick={() => { setActive((a) => Math.min(gallery.length - 1, a + 1)); setImgLoaded(false); }}
                  disabled={active === gallery.length - 1}
                  aria-label="Next photo"
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-white/85 p-2 text-slate-700 shadow-md backdrop-blur transition hover:bg-white active:scale-90 disabled:opacity-0"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
                <div data-testid="gallery-counter" className="absolute bottom-3 right-3 rounded-full bg-slate-900/70 px-2.5 py-1 text-[11px] font-bold text-white backdrop-blur">
                  {active + 1} / {gallery.length}
                </div>
                <div className="absolute bottom-3.5 left-1/2 flex -translate-x-1/2 gap-1.5">
                  {gallery.map((_, i) => (
                    <button
                      key={i}
                      data-testid={`gallery-dot-${i}`}
                      onClick={() => { setActive(i); setImgLoaded(false); }}
                      aria-label={`Photo ${i + 1}`}
                      className={`h-1.5 rounded-full transition-all ${i === active ? "w-5 bg-teal-600" : "w-1.5 bg-slate-400/70 hover:bg-slate-500"}`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
          {gallery.length > 1 && (
            <div className="mt-4 flex gap-3 overflow-x-auto pb-1">
              {gallery.map((g, i) => (
                <button
                  key={g.id || i}
                  data-testid={`product-thumb-${i}`}
                  onClick={() => { setActive(i); setImgLoaded(false); }}
                  className={`h-20 w-20 shrink-0 overflow-hidden rounded-xl border-2 transition ${
                    i === active ? "border-teal-600" : "border-transparent opacity-70 hover:opacity-100"
                  }`}
                >
                  <img src={mediaUrl(g.thumb || g.url)} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center rounded-full bg-teal-100 px-2.5 py-0.5 text-xs font-medium text-teal-800 capitalize">
              {product.category}
            </span>
            {product.ready_to_ship && (
              <span className="inline-flex items-center rounded-full bg-sky-100 px-2.5 py-0.5 text-xs font-medium text-sky-800">
                Ready to ship
              </span>
            )}
            {product.customizable && (
              <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">
                Customizable
              </span>
            )}
          </div>

          {/* Price tiers — app style: price above, quantity range below, selected underlined */}
          <div data-testid="price-tiers" className="mt-5 flex items-start gap-8 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {priceTiers.map((tier, i) => {
              const next = priceTiers[i + 1];
              const unit = product.unit || "pieces";
              const label = next ? `${tier.min_qty}–${next.min_qty - 1} ${unit}` : `≥ ${tier.min_qty} ${unit}`;
              const selected = i === tierIdx;
              return (
                <button
                  key={i}
                  data-testid={`price-tier-${i}`}
                  type="button"
                  onClick={() => { setTierIdx(i); setQty(tier.min_qty); }}
                  className="shrink-0 text-left transition active:scale-95"
                >
                  <p {...(i === 0 ? { "data-testid": "product-price" } : {})} className={`text-2xl sm:text-3xl font-extrabold tracking-tight ${selected ? "text-slate-900" : "text-slate-700"}`}>
                    {fmtTier(tier.price)}
                  </p>
                  <p className={`mt-1 inline-block pb-1 text-sm ${selected ? "border-b-[3px] border-slate-900 font-semibold text-slate-800" : "text-slate-400"}`}>
                    {label}
                  </p>
                </button>
              );
            })}
          </div>

          <h1 className="font-heading mt-3 text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 leading-tight">
            {product.title}
          </h1>
          <div className="mt-3">
            <span data-testid="product-rating-pill" className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3.5 py-1.5 text-sm font-medium text-slate-700">
              <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
              {product.rating || 0} • {product.sold || 0} sold
            </span>
          </div>

          {product.colors?.length > 0 && (
            <div className="mt-5">
              <p className="text-sm font-medium text-slate-700">{t("choose_color").replace(" first", "")}</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {product.colors.map((c) => (
                  <button
                    key={c.name}
                    data-testid={`color-option-${c.name}`}
                    onClick={() => setColor(c.name)}
                    className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                      color === c.name ? "border-teal-600 bg-teal-50 text-teal-800 ring-2 ring-teal-500/20" : "border-slate-200 text-slate-600 hover:border-teal-400"
                    }`}
                  >
                    {c.hex && <span className="h-3.5 w-3.5 rounded-full border border-slate-200" style={{ backgroundColor: c.hex }} />}
                    {c.name}
                  </button>
                ))}
              </div>
            </div>
          )}
          {product.sizes?.length > 0 && (
            <div className="mt-4">
              <p className="text-sm font-medium text-slate-700">{t("choose_size").replace(" first", "")}</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {product.sizes.map((s) => (
                  <button
                    key={s}
                    data-testid={`size-option-${s}`}
                    onClick={() => setSize(s)}
                    className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition ${
                      size === s ? "border-teal-600 bg-teal-50 text-teal-800 ring-2 ring-teal-500/20" : "border-slate-200 text-slate-600 hover:border-teal-400"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {product.volumes?.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {product.volumes.map((v) => (
                <button
                  key={v}
                  data-testid={`volume-option-${v}`}
                  onClick={() => setVolume(v)}
                  className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition ${
                    volume === v ? "border-teal-600 bg-teal-50 text-teal-800" : "border-slate-200 text-slate-600 hover:border-teal-400"
                  }`}
                >
                  {v}
                </button>
              ))}
            </div>
          )}

          <div className="mt-5 flex items-center gap-3">
            <span className="text-sm font-medium text-slate-700">{t("qty")}</span>
            <div className="flex items-center gap-2 rounded-full border border-slate-200 px-2 py-1">
              <button
                data-testid="qty-minus"
                onClick={() => setQty(Math.max(product.moq || 1, qty - 1))}
                className="rounded-full p-1 text-slate-600 hover:text-teal-700"
                aria-label="Decrease"
              >
                <Minus className="h-4 w-4" />
              </button>
              <span data-testid="qty-value" className="w-10 text-center text-sm font-bold">{qty}</span>
              <button
                data-testid="qty-plus"
                onClick={() => setQty(qty + 1)}
                className="rounded-full p-1 text-slate-600 hover:text-teal-700"
                aria-label="Increase"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="mt-6 hidden sm:grid grid-cols-3 gap-3">
            {features.orders !== false && (
            <button
              data-testid="place-order-button"
              onClick={() => setOrderOpen(true)}
              className="rounded-xl bg-teal-700 py-3.5 text-sm font-bold text-white shadow-lg shadow-teal-700/25 hover:bg-teal-800 transition active:scale-95"
            >
              {t("place_order")}
            </button>
            )}
            {features.cart !== false && (
            <button
              data-testid="add-to-cart-button"
              onClick={addToCart}
              disabled={adding}
              className="flex items-center justify-center gap-2 rounded-xl border-2 border-teal-700 py-3.5 text-sm font-bold text-teal-700 hover:bg-teal-50 transition active:scale-95 disabled:opacity-60"
            >
              <ShoppingCart className="h-4 w-4" /> {adding ? "..." : t("add_to_cart")}
            </button>
            )}
            {features.rfq !== false && (
            <button
              data-testid="request-quote-button"
              onClick={() => setQuoteOpen(true)}
              className="rounded-xl border-2 border-slate-300 py-3.5 text-sm font-bold text-slate-700 hover:border-teal-400 hover:text-teal-700 transition active:scale-95"
            >
              {t("request_quote")}
            </button>
            )}
          </div>

          <Link
            to={`/store/${product.supplier_id}`}
            data-testid="supplier-store-link"
            className="mt-6 flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-4 transition hover:border-teal-400 hover:shadow-sm"
          >
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-teal-700/10 text-teal-700 font-heading font-bold text-lg">
              {(product.supplier_name || "S")[0]}
            </div>
            <div className="min-w-0">
              <p className="flex items-center gap-1.5 font-heading font-semibold text-slate-900 truncate">
                {product.supplier_name}
                {product.verified && <BadgeCheck data-testid="supplier-verified-badge" className="h-4 w-4 text-teal-600" />}
                {liveStream && (
                  <span data-testid="supplier-live-badge" className="flex items-center gap-1 rounded-full bg-red-600 px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-wide text-white">
                    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" /> Live
                  </span>
                )}
              </p>
              <p className="flex items-center gap-1 text-xs text-slate-500">
                <MapPin className="h-3 w-3" /> {product.country}
                {product.supplier_years > 0 && <span> · {product.supplier_years} {t("yr_on")}</span>}
              </p>
            </div>
            {product.verified && (
              <span className="ml-auto hidden sm:flex items-center gap-1 text-xs font-medium text-teal-700">
                <ShieldCheck className="h-4 w-4" /> {t("verified")}
              </span>
            )}
          </Link>
          <button
            data-testid="message-supplier-button"
            onClick={() => setChatOpen(true)}
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border border-teal-900/15 bg-white py-2.5 text-sm font-semibold text-teal-800 hover:bg-teal-50 transition active:scale-95"
          >
            <MessageCircle className="h-4 w-4" /> {t("message_supplier")}
          </button>

          {product.description && (
            <div className="mt-8">
              <h2 className="font-heading text-xl font-semibold text-slate-900">{t("about_product")}</h2>
              <p data-testid="product-description" className="mt-3 text-sm sm:text-base text-slate-600 leading-relaxed whitespace-pre-line">
                {product.description}
              </p>
            </div>
          )}

          {product.attributes?.length > 0 && (
            <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200">
              {product.attributes.map((a, i) => (
                <div key={i} className="flex px-4 py-2.5 text-sm odd:bg-slate-50">
                  <span className="w-40 shrink-0 font-medium text-slate-700">{a.k}</span>
                  <span className="text-slate-600">{a.v}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {related.length > 0 && (
        <section className="mt-16">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mb-6">
            {t("more_in")} {product.category}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6">
            {related.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </section>
      )}

      {/* spacer so content clears the sticky action bar on mobile */}
      <div className="h-24 md:hidden" />

      {/* Sticky action bar — laid out like the app */}
      <div data-testid="product-action-bar" className="fixed inset-x-0 bottom-16 z-40 border-t border-slate-200 bg-white/95 backdrop-blur-md md:hidden">
        <div className="flex items-center gap-2 px-4 py-2.5">
          <button
            data-testid="bar-chat"
            type="button"
            onClick={() => setChatOpen(true)}
            className="flex flex-1 items-center justify-center gap-1.5 rounded-full border-2 border-teal-700 py-2.5 text-xs font-bold text-teal-700 transition active:scale-95"
          >
            <MessageCircle className="h-4 w-4" /> {t("bar_chat")}
          </button>
          <div className="flex flex-[2] gap-2">
            {features.cart !== false && (
              <button
                data-testid="bar-add-cart"
                type="button"
                onClick={addToCart}
                disabled={adding}
                className="flex-1 rounded-full border-2 border-teal-700 py-2.5 text-xs font-bold text-teal-700 transition active:scale-95 disabled:opacity-60"
              >
                {adding ? "..." : t("add_to_cart")}
              </button>
            )}
            {features.orders !== false && (
              <button
                data-testid="bar-order-now"
                type="button"
                onClick={() => setOrderOpen(true)}
                className="flex-1 rounded-full bg-teal-700 py-2.5 text-xs font-bold text-white shadow-md shadow-teal-700/25 transition active:scale-95"
              >
                {t("place_order")}
              </button>
            )}
          </div>
        </div>
      </div>

      <QuoteDialog product={product} open={quoteOpen} onOpenChange={setQuoteOpen} />
      <OrderDialog product={product} open={orderOpen} onOpenChange={setOrderOpen} />
      <ChatDialog product={product} open={chatOpen} onOpenChange={setChatOpen} />
    </div>
  );
}
